import React, { useCallback, useEffect, useState } from 'react';
import { Pencil, PlusCircle, Trash2 } from 'lucide-react';
import { embeddingProviderService, type EmbeddingProvider } from '../../../components/api/embeddingProviderService';
import AddEmbeddingProviderModal from './embeddings/AddEmbeddingProviderModal';

const Embeddings: React.FC = () => {
  const [list, setList] = useState<EmbeddingProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editProvider, setEditProvider] = useState<EmbeddingProvider | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [toggleProvider, setToggleProvider] = useState<EmbeddingProvider | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await embeddingProviderService.getList();
      setList(Array.isArray(res) ? res : []);
    } catch {
      setError('Failed to load embedding providers.');
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleCloseModal = () => {
    setShowModal(false);
    setEditProvider(null);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await embeddingProviderService.delete(deleteId);
      await fetchList();
      setDeleteId(null);
    } catch {
      setError('Failed to delete provider.');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleConfirm = async () => {
    if (!toggleProvider) return;
    const p = toggleProvider;
    setTogglingId(p.id);
    setToggleProvider(null);
    try {
      await embeddingProviderService.setActive(p.id, !p.isActive);
      await fetchList();
    } catch {
      setError('Failed to update status.');
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-white">Embedding Providers</h2>
          <p className="text-sm text-gray-400 mt-1">Manage embedding providers used for vectorization.</p>
        </div>
        <button
          onClick={() => { setEditProvider(null); setShowModal(true); }}
          className="flex items-center bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm shadow"
        >
          <PlusCircle className="w-4 h-4 mr-2" /> Add New Embedding Provider
        </button>
      </div>

      {error && <p className="mb-3 text-sm text-red-400">{error}</p>}

      <div className="overflow-x-auto border border-neutral-800 rounded">
        <table className="min-w-full text-sm text-left text-gray-300">
          <thead className="bg-neutral-900 text-gray-400">
            <tr>
              <th className="px-4 py-2">Display Name</th>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Default Model</th>
              <th className="px-4 py-2">Dimensions</th>
              <th className="px-4 py-2">API Version</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500">Loading…</td></tr>
            ) : list.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500">No embedding providers yet.</td></tr>
            ) : (
              list.map((p) => (
                <tr key={p.id} className="border-t border-neutral-800 hover:bg-neutral-800/30">
                  <td className="px-4 py-3">{p.displayName}</td>
                  <td className="px-4 py-3">{p.name}</td>
                  <td className="px-4 py-3">{p.defaultModel ?? '—'}</td>
                  <td className="px-4 py-3">{p.defaultVectorDimensions ?? '—'}</td>
                  <td className="px-4 py-3">{p.apiVersion ?? '—'}</td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => setToggleProvider(p)}
                      disabled={togglingId === p.id}
                      className={`inline-block px-2 py-1 rounded text-xs font-medium cursor-pointer disabled:opacity-50 ${p.isActive ? 'bg-green-700 text-green-100 hover:bg-green-600' : 'bg-red-700 text-red-100 hover:bg-red-600'}`}
                      title={p.isActive ? 'Set inactive' : 'Set active'}
                    >
                      {togglingId === p.id ? '…' : p.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-4 py-3 flex justify-end gap-2">
                    <button className="text-yellow-400 hover:text-white" title="Edit" onClick={() => { setEditProvider(p); setShowModal(true); }}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button className="text-red-400 hover:text-red-200" title="Delete" onClick={() => setDeleteId(p.id)}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <AddEmbeddingProviderModal
          onClose={handleCloseModal}
          onSubmit={fetchList}
          initialData={editProvider}
        />
      )}

      {deleteId && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-neutral-900 rounded-xl border border-neutral-800 p-6 max-w-sm w-full">
            <h3 className="text-white font-semibold mb-2">Delete embedding provider?</h3>
            <p className="text-gray-400 text-sm mb-4">This cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteId(null)} className="text-gray-400 hover:text-white text-sm" disabled={deleting}>
                Cancel
              </button>
              <button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm disabled:opacity-50" disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toggleProvider && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-neutral-900 rounded-xl border border-neutral-800 p-6 max-w-sm w-full">
            <h3 className="text-white font-semibold mb-2">
              {toggleProvider.isActive ? 'Set provider to inactive?' : 'Set provider to active?'}
            </h3>
            <p className="text-gray-400 text-sm mb-4">
              {toggleProvider.isActive ? 'This provider will be disabled.' : 'This provider will be enabled.'}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setToggleProvider(null)}
                className="text-gray-400 hover:text-white text-sm"
                disabled={togglingId === toggleProvider.id}
              >
                Cancel
              </button>
              <button
                onClick={handleToggleConfirm}
                className={toggleProvider.isActive ? 'bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded text-sm disabled:opacity-50' : 'bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm disabled:opacity-50'}
                disabled={togglingId === toggleProvider.id}
              >
                {togglingId === toggleProvider.id ? 'Updating…' : toggleProvider.isActive ? 'Yes, set to Inactive' : 'Yes, set to Active'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Embeddings;

